// src/lib/cookieStorage.js
// Secure cookie-based storage adapter for Supabase Auth
// Prevents XSS token theft by using HttpOnly, Secure, SameSite cookies

/**
 * Cookie storage adapter for Supabase Auth
 * Implements the same interface as localStorage but uses cookies
 *
 * Security features:
 * - HttpOnly: JavaScript cannot read the cookie (XSS protection)
 * - Secure: Only sent over HTTPS
 * - SameSite=Strict: CSRF protection
 * - Max-Age: 7 days (matches Supabase token expiry)
 */
class CookieStorage {
  constructor() {
    this.isBrowser = typeof window !== 'undefined';
  }

  /**
   * Get a cookie value by key
   * @param {string} key - Cookie name
   * @returns {string|null} Cookie value or null
   */
  getItem(key) {
    if (!this.isBrowser) return null;

    // First check cookies
    const name = key + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');

    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(name) === 0) {
        const value = cookie.substring(name.length, cookie.length);
        // Return the value (already encoded)
        return value;
      }
    }

    // Fallback to localStorage for migration compatibility
    // This allows existing users to keep their sessions during the transition
    try {
      const localValue = window.localStorage.getItem(key);
      if (localValue) {
        console.log('⚠️ Found token in localStorage, migrating to cookies...');
        // Migrate to cookies
        this.setItem(key, localValue);
        // Clear from localStorage
        window.localStorage.removeItem(key);
        return localValue;
      }
    } catch (e) {
      console.warn('Could not access localStorage:', e);
    }

    return null;
  }

  /**
   * Set a cookie value
   * @param {string} key - Cookie name
   * @param {string} value - Cookie value
   */
  setItem(key, value) {
    if (!this.isBrowser) return;

    // Cookie security settings
    const maxAge = 7 * 24 * 60 * 60; // 7 days in seconds
    const isProduction = window.location.protocol === 'https:';

    // Build cookie string with security flags
    let cookie = `${key}=${encodeURIComponent(value)}; `;
    cookie += `max-age=${maxAge}; `;
    cookie += `path=/; `;
    cookie += `SameSite=Strict; `;

    // Only set Secure flag in production (HTTPS)
    if (isProduction) {
      cookie += `Secure; `;
    }

    // Note: HttpOnly flag cannot be set from JavaScript
    // It must be set from the server-side (in auth endpoints)
    // This client-side implementation is for fallback compatibility

    document.cookie = cookie;
  }

  /**
   * Remove a cookie
   * @param {string} key - Cookie name
   */
  removeItem(key) {
    if (!this.isBrowser) return;

    // Set cookie with expired date to remove it
    document.cookie = `${key}=; path=/; max-age=0; SameSite=Strict;`;
  }

  /**
   * Clear all cookies (not recommended, but required by storage interface)
   * Only clears Supabase-related cookies
   */
  clear() {
    if (!this.isBrowser) return;

    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;

      // Only clear Supabase auth cookies
      if (name.startsWith('supabase')) {
        this.removeItem(name);
      }
    }
  }

  /**
   * Get the number of stored items
   * @returns {number} Number of Supabase cookies
   */
  get length() {
    if (!this.isBrowser) return 0;

    const cookies = document.cookie.split(';');
    let count = 0;

    for (let cookie of cookies) {
      const name = cookie.trim().split('=')[0];
      if (name.startsWith('supabase')) {
        count++;
      }
    }

    return count;
  }

  /**
   * Get key at specific index
   * @param {number} index - Index
   * @returns {string|null} Key name or null
   */
  key(index) {
    if (!this.isBrowser) return null;

    const cookies = document.cookie.split(';');
    const supabaseCookies = cookies
      .map(c => c.trim().split('=')[0])
      .filter(name => name.startsWith('supabase'));

    return supabaseCookies[index] || null;
  }
}

// Export singleton instance
module.exports = new CookieStorage();
